import { StateCreator } from "zustand";

import { AvCallSliceT, IAvCall, IAvCallState } from "~/src/zustand/IAvCallSlice";
import { StoreT } from "~/src/zustand/IZustand";

import { ECallState } from "../interfaces/IAVCall";

export const initialState: IAvCallState = {
	avCalls: []
};

export const createAvCallSlice: StateCreator<
	StoreT,
	[["zustand/devtools", never], ["zustand/subscribeWithSelector", never]],
	[],
	AvCallSliceT
> = (set) => ({
	...initialState,
	avCallAdd: (avCall: IAvCall) => {
		set(
			(prev) => {
				const existing = prev.avCalls.find((item) => item.id === avCall.id);
				if (!existing) {
					return {
						...prev,
						avCalls: [...prev.avCalls, avCall]
					};
				}
				return prev;
			},
			false,
			"avCallAdd"
		);
	},
	avCallRemove: (callId: string) => {
		set(
			(prev) => {
				return {
					...prev,
					avCalls: prev.avCalls.filter((item) => item.id !== callId)
				};
			},
			false,
			"avCallRemove"
		);
	},
	avCallSetCallState: ({ id, callState }: { id: string; callState: ECallState }) => {
		set(
			(prev) => {
				return {
					...prev,
					avCalls: prev.avCalls.map((item) => {
						if (item.id === id) {
							return {
								...item,
								callState
							};
						}
						return item;
					})
				};
			},
			false,
			"avCallSetCallState"
		);
	},
	avCallSetLocalAudioTrack: ({ id, trackID }: { id: string; trackID: string | undefined }) => {
		set(
			(prev) => {
				return {
					...prev,
					avCalls: prev.avCalls.map((item) => {
						if (item.id === id) {
							return {
								...item,
								trackID
							};
						}
						return item;
					})
				};
			},
			false,
			"avCallSetLocalAudioTrack"
		);
	},
	avCallSetLocalVideoTrack: ({ id, trackID }: { id: string; trackID: string | undefined }) => {
		set(
			(prev) => {
				return {
					...prev,
					avCalls: prev.avCalls.map((item) => {
						if (item.id === id) {
							return {
								...item,
								localVideoTrackID: trackID
							};
						}
						return item;
					})
				};
			},
			false,
			"avCallSetLocalVideoTrack"
		);
	},
	avCallSetRemoteAudioTrack: ({ id, trackID }: { id: string; trackID: string | undefined }) => {
		set(
			(prev) => {
				return {
					...prev,
					avCalls: prev.avCalls.map((item) => {
						if (item.id === id) {
							return {
								...item,
								remoteAudioTrackID: trackID
							};
						}
						return item;
					})
				};
			},
			false,
			"avCallSetRemoteAudioTrack"
		);
	},
	avCallSetRemoteVideoTrack: ({ id, trackID }: { id: string; trackID: string | undefined }) => {
		set(
			(prev) => {
				return {
					...prev,
					avCalls: prev.avCalls.map((item) => {
						if (item.id === id) {
							return {
								...item,
								remoteVideoTrackID: trackID
							};
						}
						return item;
					})
				};
			},
			false,
			"avCallSetRemoteVideoTrack"
		);
	},
	avCallSetSelectedCam: (selectedCam: string | undefined) => {
		set(
			(prev) => {
				return {
					...prev,
					selectedCam
				};
			},
			false,
			"avCallSetSelectedCam"
		);
	},
	avCallSetSelectedMic: (selectedMic: string | undefined) => {
		set(
			(prev) => {
				return {
					...prev,
					selectedMic
				};
			},
			false,
			"avCallSetSelectedMic"
		);
	},
	avCallSetSelectedSpeaker: (selectedSpeaker: string | undefined) => {
		set(
			(prev) => {
				return {
					...prev,
					selectedSpeaker
				};
			},
			false,
			"avCallSetSelectedSpeaker"
		);
	},
	avCallSetIsMinimized: ({ id, isMinimized }: { id: string; isMinimized: true | undefined }) => {
		set(
			(prev) => {
				return {
					...prev,
					avCalls: prev.avCalls.map((item) => {
						if (item.id === id) {
							return {
								...item,
								isMinimized
							};
						}
						return item;
					})
				};
			},
			false,
			"avCallSetIsMinimized"
		);
	},
	avCallSetIsFullscreen: ({ id, isFullscreen }: { id: string; isFullscreen: true | undefined }) => {
		set(
			(prev) => {
				return {
					...prev,
					avCalls: prev.avCalls.map((item) => {
						if (item.id === id) {
							return {
								...item,
								isFullscreen
							};
						}
						return item;
					})
				};
			},
			false,
			"avCallSetIsFullscreen"
		);
	},
	avCallToggleBottomBar: ({
		id,
		showBottomBarInFullscreen
	}: {
		id: string;
		showBottomBarInFullscreen: true | undefined;
	}) => {
		set(
			(prev) => {
				return {
					...prev,
					avCalls: prev.avCalls.map((item) => {
						if (item.id === id) {
							return {
								...item,
								showBottomBarInFullscreen
							};
						}
						return item;
					})
				};
			},
			false,
			"avCallToggleBottomBar"
		);
	},
	avCallSetRemoteAudioMuted: ({ isRemoteAudioMuted }: { id: string; isRemoteAudioMuted: boolean }) => {
		set(
			(prev) => {
				return {
					...prev,
					isRemoteAudioMuted
				};
			},
			false,
			"avCallSetRemoteAudioMuted"
		);
	},
	avCallSetRemoteVideoMuted: ({ id, isRemoteVideoMuted }: { id: string; isRemoteVideoMuted: boolean }) => {
		set(
			(prev) => {
				return {
					...prev,
					avCalls: prev.avCalls.map((item) => {
						if (item.id === id) {
							return {
								...item,
								isRemoteVideoMuted
							};
						}
						return item;
					})
				};
			},
			false,
			"avCallSetRemoteVideoMuted"
		);
	}
});
